import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom'; // Added useNavigate
import { createPageUrl } from '@/utils';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Youtube, Leaf, ArrowRight, Loader2, Check } from 'lucide-react'; // Added Loader2, Check
import Logo from '@/components/common/Logo';
// import { useAuth } from '@/context/AuthContext'; // Removed as file does not exist

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  // const { login } = useAuth(); // Removed

  // State for subscription flow
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const footerLinks = {
    company: [
      { name: t('footer.links.about') || 'About Us', path: '/about' },
      { name: t('nav.marketplace') || 'Marketplace', path: '/marketplace' },
      { name: t('footer.links.contact') || 'Contact', path: '/contact' },
      { name: t('footer.links.careers') || 'Careers', path: '#' },
      { name: t('footer.links.blog') || 'Blog', path: '/blog' }
    ],
    legal: [
      { name: t('footer.links.privacy') || 'Privacy Policy', path: '/privacy' },
      { name: t('footer.links.terms') || 'Terms of Service', path: '/terms' },
      { name: t('footer.links.cookie') || 'Cookie Policy', path: '/cookies' },
      { name: t('footer.links.disclaimer') || 'Disclaimer', path: '/disclaimer' }
    ],
    support: [
      { name: t('footer.links.help') || 'Help Center', path: '/faq' },
      { name: t('footer.links.faqs') || 'FAQs', path: '/faq' },
      { name: t('footer.links.shipping') || 'Shipping Info', path: '#' },
      { name: t('footer.links.returns') || 'Returns', path: '#' }
    ],
    farmers: [
      { name: t('footer.links.seller') || 'Become a Seller', path: '/farmer/register' },
      { name: t('footer.links.dashboard') || 'Farmer Dashboard', path: '/farmer/dashboard' },
      { name: t('footer.links.guidelines') || 'Seller Guidelines', path: '#' },
      { name: t('footer.links.pricing') || 'Pricing', path: '#' }
    ]
  };

  // --- Logic: Handle Subscription ---
  const handleSubscribe = async () => {
    setErrorMessage('');

    // 1. Basic Email Validation Regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');

    try {
      // 2. Subscribe to newsletter
      const subscribeResponse = await fetch('/api/newsletter-subscribers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          name: email.split('@')[0],
          source: 'footer_form'
        }),
      });

      const subscribeData = await subscribeResponse.json();

      if (!subscribeResponse.ok || !subscribeData.success) {
        throw new Error(subscribeData.error || 'Failed to subscribe');
      }

      // 3. Create user account or check if exists
      try {
        const userCheckResponse = await fetch('/api/auth/create-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            full_name: email.split('@')[0],
            source: 'newsletter_subscription',
            auto_login: true
          }),
        });

        const userData = await userCheckResponse.json();

        // 4. Auto-login the user
        if (userData.success && userData.session) {
          // Store session in localStorage
          localStorage.setItem('hamro_subscriber_session', JSON.stringify({
            email: email,
            full_name: email.split('@')[0],
            role: 'buyer',
            subscribed_via: 'newsletter'
          }));

          // Dispatch event to notify app of login
          window.dispatchEvent(new Event('subscriber-login'));

          // 5. Success - Show success message and redirect
          setStatus('success');

          // Redirect to marketplace after 2 seconds
          setTimeout(() => {
            navigate('/marketplace');
          }, 2000);
        } else {
          // Just show subscription success without login
          setStatus('success');
          setTimeout(() => {
            setEmail('');
            setStatus('idle');
          }, 3000);
        }
      } catch (userError) {
        console.error('User creation error:', userError);
        // Still show subscription success even if user creation fails
        setStatus('success');
        setTimeout(() => {
          setEmail('');
          setStatus('idle');
        }, 3000);
      }

    } catch (error) {
      console.error("Subscription error:", error);
      setStatus('error');
      setErrorMessage(error.message || 'Something went wrong. Please try again.');

      // Reset error after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  return (
    <footer className="relative bg-white text-slate-600 font-sans border-t border-gray-100">
      {/* Decorative Texture - Kept Light & Airy */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-b from-green-50/80 to-transparent rounded-full blur-[100px] translate-x-1/3 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-t from-green-50/80 to-transparent rounded-full blur-[80px] -translate-x-1/3 translate-y-1/3" />
      </div>

      {/* Newsletter Section - Simplified Clean Banner */}
      <div className="relative z-10 pt-8 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#6B2D8B] to-[#b91c1c] rounded-[2rem] px-8 py-6 shadow-2xl shadow-purple-900/10 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative border border-purple-800/20">

            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-[0.1] bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,#fff_10px,#fff_11px)] mix-blend-overlay" />

            {/* Left Content */}
            <div className="relative text-center md:text-left z-10">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <Leaf className="w-5 h-5 text-white" />
                <h3 className="text-xl md:text-2xl font-bold text-white">{t('footer.newsletter.title')}</h3>
              </div>
              <p className="text-purple-100/90 text-sm md:text-base font-medium drop-shadow-sm">Get the latest updates on fresh products and special offers</p>
            </div>

            {/* Right: Input */}
            <div className="relative z-10 w-full md:w-auto md:min-w-[400px]">
              <div className="flex items-center gap-2 bg-white rounded-full p-1.5 shadow-md">
                <div className="flex-1 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === 'loading' || status === 'success'}
                    placeholder={t('footer.newsletter.placeholder')}
                    className="w-full pl-11 pr-4 py-2.5 bg-transparent border-none focus:outline-none text-slate-700 placeholder:text-slate-400 text-sm"
                  />
                </div>
                <button
                  onClick={handleSubscribe}
                  disabled={status === 'loading' || status === 'success'}
                  className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all flex items-center gap-1.5 whitespace-nowrap shadow-md hover:shadow-lg ${status === 'success'
                    ? 'bg-green-500 text-white cursor-default'
                    : 'bg-[#F5C842] text-[#3B1F00] hover:brightness-110 hover:-translate-y-0.5 active:scale-95'
                    }`}
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : status === 'success' ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('footer.newsletter.success')}
                    </>
                  ) : (
                    <>
                      {t('footer.newsletter.button')}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              {/* Error/Success Messages */}
              {errorMessage && (
                <p className="text-red-100 text-xs mt-2 text-center md:text-right">{errorMessage}</p>
              )}
              {status === 'success' && (
                <p className="text-white text-xs mt-2 text-center md:text-right flex items-center justify-center md:justify-end gap-1">
                  <Check className="w-3 h-3" /> {t('footer.newsletter.success_msg')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-8">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-5">
            <Link to={createPageUrl('Home')} className="flex items-center gap-3 group w-fit">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shadow-green-200 group-hover:scale-105 transition-transform">
                <Logo className="w-8 h-8 brightness-200 grayscale-0 object-contain" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-slate-900 leading-none tracking-tight">HamroKrishi</span>
                <span className="text-[10px] font-bold text-green-600 tracking-[0.2em] mt-0.5">BAZZAR</span>
              </div>
            </Link>
            <p className="text-slate-500 leading-relaxed text-sm max-w-sm font-medium">
              {t('footer.brand.desc')}
            </p>
            <div className="flex gap-3">
              {[
                {
                  name: 'Facebook',
                  icon: Facebook,
                  href: 'https://facebook.com/profile.php?id=61584566854466',
                  className: 'bg-[#1877F2] hover:bg-[#166fe5]',
                },
                {
                  name: 'Twitter',
                  icon: Twitter,
                  href: 'https://x.com/govinsingh3406',
                  className: 'bg-[#1DA1F2] hover:bg-[#1a91da]',
                },
                {
                  name: 'Instagram',
                  icon: Instagram,
                  href: 'https://instagram.com/hamrokrishibazzar/',
                  className: 'bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90',
                },
                {
                  name: 'YouTube',
                  icon: Youtube,
                  href: 'https://www.youtube.com/@HamroKrishiBazzar?sub_confirmation=1',
                  className: 'bg-[#FF0000] hover:bg-[#e60000]',
                },
                {
                  name: 'TikTok',
                  icon: () => (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  ),
                  href: 'https://www.tiktok.com/@kirshibazar',
                  className: 'bg-black hover:bg-gray-800',
                },
                {
                  name: 'WhatsApp',
                  icon: () => (
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
                      alt="WhatsApp" 
                      className="w-5 h-5 brightness-200 grayscale-0"
                    />
                  ),
                  href: 'https://wa.me/9779849047275',
                  className: 'bg-[#25D366] hover:bg-[#20bd5a]',
                }
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-white shadow-md transition-all duration-300 hover:scale-110 hover:shadow-lg ${social.className}`}
                  aria-label={social.name}
                >
                  {typeof social.icon === 'function' ? <social.icon /> : <social.icon className="w-4 h-4" />}
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          {[
            { title: t('footer.links.company') || 'Company', links: footerLinks.company },
            { title: t('footer.links.legal') || 'Legal', links: footerLinks.legal },
            { title: t('footer.links.support') || 'Support', links: footerLinks.support },
            { title: t('footer.links.farmers') || 'For Farmers', links: footerLinks.farmers }
          ].map((col, idx) => (
            <div key={idx} className="flex flex-col">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-4">{col.title}</h3>
              <ul className="space-y-3">
                {col.links.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="text-slate-500 hover:text-green-600 text-sm font-medium transition-colors flex items-center gap-2 group transform hover:translate-x-1 duration-200"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-green-500 transition-colors" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-5 border-t border-gray-100">
          {[
            { icon: Mail, label: t('footer.contact.email') || 'Email Us', value: 'support@hamrokrishibazzar.com', link: 'mailto:support@hamrokrishibazzar.com' },
            { icon: Phone, label: t('footer.contact.call') || 'Call Us', value: '+977-9849047275', link: 'tel:+9779849047275' },
            { icon: MapPin, label: t('footer.contact.visit') || 'Visit Us', value: t('footer.contact.address'), link: '#' }
          ].map((item, idx) => (
            <a
              key={idx}
              href={item.link}
              className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-lg hover:shadow-green-100/50 hover:border-green-100 transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm text-slate-400 group-hover:bg-green-600 group-hover:text-white transition-all">
                <item.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 group-hover:text-green-600 transition-colors mb-0.5">{item.label}</p>
                <p className="text-sm font-bold text-slate-800">{item.value}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Bottom Copyright */}
        <div className="border-t border-gray-100 pt-5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-medium text-slate-500">
          <p>{t('footer.copyright', { year: currentYear })}</p>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-green-600 transition-colors">{t('footer.links.privacy')}</Link>
            <Link to="/terms" className="hover:text-green-600 transition-colors">{t('footer.links.terms')}</Link>
            <Link to="/cookies" className="hover:text-green-600 transition-colors">{t('footer.links.cookie')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
